import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ModalHeader } from 'shared/ModalHeader';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { getSharedLinkStats } from 'services/fileManager/fileManagerServices';
import moment from 'moment';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { convertToSpecificTimeZoneFormat, convertToUTC } from 'utils/utility_functions/timezone';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { Tooltip } from 'primereact/tooltip';

export const FileSharingDetailsModal = ({ show, onHide, selectedFile, setSelectedFile }) => {
  const userContext = useUserDetailsContext();
  const [selectedLog, setSelectedLog] = useState([]);
  const [selectedLinkDetails, setSelectedLinkDetails] = useState([]);
  const [buttonClicked, setButtonClicked] = useState(false);
  const { addToast } = useToast();

  const currentDateUTC = moment(convertToUTC(userContext?.userDetails?.timezone));
  const currentDateString = currentDateUTC.toISOString();

  const handleEyeClick = async (rowData) => {
    setButtonClicked(true);
    try {
      const response = await getSharedLinkStats({ lawft_link_id: rowData?.lawft_link_id });
      setSelectedLog(response?.data);
    } catch {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LOAD_ACTIVITY_LOGS_FAILED);
    }
  };

  const handleCopyToClipboard = async (text) => {
    if (text) {
      try {
        await navigator.clipboard.writeText(text);
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CLIPBOARD_COPY_SUCCCESS);
      } catch {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CLIPBOARD_COPY_FAIL);
      }
    } else {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.message.CLIPBOARD_COPY_FAIL);
    }
  };

  const handleFileSharingDetailsModalClose = () => {
    setSelectedLog(null);
    setButtonClicked(false);
    setSelectedLinkDetails(null);
    setSelectedFile(null);
    onHide();
  };

  const getActivityLogs = () => {
    return selectedLog?.file_view?.length > 0 || selectedLog?.file_download?.length > 0
      ? selectedLog?.file_view?.map((_, index) => ({
          file_view: selectedLog?.file_view?.[index],
          file_download: selectedLog?.file_download?.[index],
        }))
      : [];
  };

  const getExpiryStatus = (expiryDate) => {
    const expiryUTC = moment.utc(expiryDate);
    const expiryDateString = expiryUTC.toISOString();

    if (expiryDateString <= currentDateString) {
      return <span className="text-expire">Expired</span>;
    }

    const duration = moment.duration(expiryUTC.diff(currentDateUTC));
    const minutes = Math.ceil(duration.asMinutes());
    const hours = Math.ceil(duration.asHours());

    if (minutes <= 0) {
      return <span className="text-expire">Expires</span>;
    } else if (minutes <= 1440 && hours <= 1) {
      return (
        <span className="text-expire">
          Expires in {minutes} {minutes === 1 ? 'minute' : 'minutes'}
        </span>
      );
    } else if (!minutes <= 1440 && hours >= 1 && hours <= 24) {
      return (
        <span className="text-expire">
          Expires in {hours} {hours === 1 ? 'hour' : 'hours'}
        </span>
      );
    } else {
      return (
        <>
          <span className="F-size12 black-600">Expires on</span>
          <br />
          <span>{convertToSpecificTimeZoneFormat(expiryUTC, userContext?.userDetails?.timezone)}</span>
        </>
      );
    }
  };

  const getActivityBody = (rowData) => {
    return (
      <>
        <div>
          <span className="F-size12 black-600 me-2">Views:</span>
          {rowData?.view_count || 0}
        </div>
        <div>
          <span className="F-size12 black-600 me-2">Downloads:</span>
          {rowData?.download_count || 0}
        </div>
      </>
    );
  };

  const actionBody = (rowData) => {
    let isExpired = false;

    const expiryUTC = moment.utc(rowData?.link_expiry);
    const expiryDateString = expiryUTC.toISOString();

    if (expiryDateString <= currentDateString) {
      isExpired = true;
    }

    return (
      <>
        <i
          id={`additional-information-tooltip-${rowData?.lawft_link_id}`}
          className="fas fa-info-circle pointer text-primary mx-2 mt-1"
          onClick={() => handleEyeClick(rowData)}
        ></i>
        <i
          id={`copy-icon-tooltip-${rowData?.lawft_link_id}`}
          className={`icon-notes custom-tooltip-report mx-2 mt-1 pointer ${isExpired ? 'text-muted' : 'text-primary-main'}`}
          onClick={() => !isExpired && handleCopyToClipboard(rowData?.lawft_link_endpoint || '')}
          style={{ pointerEvents: isExpired ? 'none' : 'auto', cursor: isExpired ? 'not-allowed' : 'pointer' }}
        ></i>

        <Tooltip target={`#additional-information-tooltip-${rowData?.lawft_link_id}`} position="top">
          {' '}
          Additional Information{' '}
        </Tooltip>
        <Tooltip target={`#copy-icon-tooltip-${rowData?.lawft_link_id}`} position="top">
          {' '}
          Copy Link{' '}
        </Tooltip>
      </>
    );
  };

  useEffect(async () => {
    if (selectedFile && selectedFile?.lawft_link_expires_on) {
      try {
        const response = await getSharedLinkStats({ file_system_sk: selectedFile?.sk });
        setSelectedLinkDetails(response?.data?.['lawft_links'] || []);
      } catch (error) {
        console.error('Error loading shared links:', error);
      }
    }
  }, [selectedFile]);

  return (
    <Dialog
      header={ModalHeader}
      closable={false}
      visible={show}
      modal
      style={{ width: '80vw' }}
      onHide={handleFileSharingDetailsModalClose}
      resizable={false}
      className="create-msg-popup"
    >
      <div className="py-4">
        <div className="modal-heading F-size16 text-bold mb-2">{selectedFile?.display_name}</div>

        <div className="d-flex">
          <p>
            {' '}
            Total Views <span className="text-bold F-size16 ms-2">{selectedFile?.view_count || 0}</span>{' '}
          </p>
          <p className="ms-4">
            {' '}
            Total Downloads <span className="text-bold F-size16 ms-2">{selectedFile?.download_count || 0}</span>{' '}
          </p>
        </div>

        <div className="d-flex align-items-start" style={{ gap: '1rem' }}>
          {/* Generated Links Section using DataTable */}
          <div
            className="flex-grow-1 file-sharedlist-table"
            style={{ maxHeight: '300px', overflowY: 'auto', width: selectedLog !== null ? '60%' : '100%', transition: 'width 0.3s ease' }}
          >
            <h5 className="caption-bold mb-2">Generated Links</h5>

            <div className="col-12 p-0 datatable-responsive">
              <DataTable value={selectedLinkDetails} className="p-datatable-responsive">
                <Column header="Lawft Link Nickname" body={(rowData) => <span>{rowData?.nickname || '-'}</span>} field="nickname" />
                <Column header="Status" body={(rowData) => <span>{getExpiryStatus(rowData?.link_expiry)}</span>} />
                <Column header="Activity" body={(rowData) => getActivityBody(rowData)} />
                <Column header="Actions" body={actionBody} />
              </DataTable>
            </div>
          </div>

          {/* File Activity Logs Section (Conditional) */}
          {selectedLog !== null && buttonClicked && (
            <div
              className="p-4 file-sharedlist-table"
              style={{ width: '35%', maxHeight: '300px', overflowY: 'auto', transition: 'width 0.3s ease' }}
            >
              <h5 className="caption-bold mb-2">File Activity Logs</h5>

              <DataTable value={getActivityLogs()}>
                <Column
                  header={`Views (${selectedLog?.file_view ? selectedLog?.file_view?.length : 0})`}
                  body={(rowData) => (
                    <span>
                      {rowData?.file_view ? convertToSpecificTimeZoneFormat(rowData?.file_view, userContext?.userDetails?.timezone) : '-'}
                    </span>
                  )}
                />
                <Column
                  header={`Downloads (${selectedLog?.file_download ? selectedLog?.file_download?.length : 0})`}
                  body={(rowData) => (
                    <span>
                      {rowData?.file_download
                        ? convertToSpecificTimeZoneFormat(rowData?.file_download, userContext?.userDetails?.timezone)
                        : '-'}
                    </span>
                  )}
                />
              </DataTable>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
